import { ALL_WHITE_SPACES_PATTERN } from "@/utils/utils";
// eslint-disable-next-line no-restricted-imports
import { InputNumber as AntInputNumber, Space } from "antd";
import { DEFAULT_LOCALE } from "../../app/i18n";
import { InputAddon } from "./form/addons/InputAddon";

const NON_DIGIT_CHARACTERS_REGEX = new RegExp("[^\\d]", "g");

const parserInteger = (value?: string) => {
  if (!value) {
    return undefined as unknown as number;
  }

  let result = "";

  if (value?.startsWith("-")) {
    result = "-";
  }

  result += value?.replace(NON_DIGIT_CHARACTERS_REGEX, "");

  return parseInt(result, 10);
};

const formatterInteger = (value: number | undefined) => {
  if (!value) {
    return "";
  }

  return Intl.NumberFormat(DEFAULT_LOCALE).format(value);
};

const formatterDecimal = (
  value: number | undefined,
  info: { userTyping: boolean; input: string },
  precision?: number,
) => {
  if (!value) {
    return "";
  }

  const result: string = info.input.replace(",", ".").replace(ALL_WHITE_SPACES_PATTERN, "");

  if (result === "-0") {
    return info?.input;
  }

  if (result === "-0.") {
    return info?.input.slice(0, -1);
  }

  return Intl.NumberFormat(DEFAULT_LOCALE, {
    minimumFractionDigits: 0,
    maximumFractionDigits: precision,
  }).format(value);
};

type Props = {
  type?: "integer" | "decimal";
  onChange?: (value: number | null) => void;
  value?: number;
  step?: number;
  disabled?: boolean;
  formatter?: (value: number | undefined, info?: Record<string, any>, precision?: number) => string;
  noFormat?: boolean;
  precision?: number;
  min?: number;
  max?: number;
  placeholder?: string;
  addon?:
    | "area"
    | "distance"
    | "engineDisplacement"
    | "enginePower"
    | "euro"
    | "million"
    | "nettoPoints"
    | "photovoltaicPower"
    | "percentage"
    | "piece"
    | "volume"
    | "kg"
    | "g";
};

export const InputNumber = ({
  value,
  onChange,
  type = "integer",
  disabled,
  step,
  formatter,
  noFormat,
  precision,
  addon,
  min,
  max,
  placeholder,
}: Props) => {
  const precisionValue = precision ?? (type === "decimal" ? 2 : undefined);
  const formatFunc = formatter ?? (type === "decimal" ? formatterDecimal : formatterInteger);

  const InputElement = (
    <AntInputNumber<number>
      style={{ width: "100%" }}
      formatter={noFormat ? undefined : (value, info) => formatFunc(value, info, precisionValue)}
      value={value}
      onChange={onChange}
      parser={type === "integer" ? parserInteger : undefined}
      precision={precisionValue}
      decimalSeparator={type === "decimal" ? "," : undefined}
      step={step}
      disabled={disabled}
      min={min}
      max={max}
      placeholder={placeholder}
    />
  );

  if (addon) {
    return (
      <Space.Compact block>
        {InputElement}

        <Space.Addon>
          <InputAddon type={addon} />
        </Space.Addon>
      </Space.Compact>
    );
  }

  return InputElement;
};
